import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../App';

// Spot Rebalancing — MANUAL by default. Spot rebalancing never happens
// automatically: this panel exposes a one-click "Rebalance to Spot" button
// (POST /v1/clusters/{id}/spot/rebalance-scan) plus an explicit, default-OFF
// "Automatic spot rebalancing" toggle (node_optimization_config.spot_rebalance_auto
// via the rebalance-config endpoint). The backend gates every auto-emitter
// (F16 pinned-excluded + periodic drift) behind that flag.
export default function SpotRebalancePanel({ clusterId }) {
  const [auto, setAuto] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    if (!clusterId) return;
    try {
      const r = await axios.get(`${API}/v1/clusters/${clusterId}/node-optimization/rebalance-config`);
      setAuto(!!r.data?.spot_rebalance_auto);
    } catch {
      /* leave default off */
    }
  }, [clusterId]);

  useEffect(() => { load(); }, [load]);

  const toggleAuto = async (val) => {
    setAuto(val);
    try {
      await axios.post(`${API}/v1/clusters/${clusterId}/node-optimization/rebalance-config`, {
        spot_rebalance_auto: val,
      });
    } catch {
      setAuto(!val); // revert on failure
    }
  };

  const rebalanceNow = async () => {
    setBusy(true);
    setMsg('');
    try {
      const r = await axios.post(`${API}/v1/clusters/${clusterId}/spot/rebalance-scan`);
      const n = r.data?.emitted ?? r.data?.actions_emitted ?? r.data?.count;
      setMsg(n != null ? `Queued ${n} spot-rebalance action(s).` : 'Spot rebalance triggered.');
    } catch (e) {
      setMsg('Failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  if (!clusterId) return null;

  return (
    <div className="bg-white rounded-lg shadow p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Spot Rebalancing</h3>
          <p className="text-sm text-gray-500 mt-1">
            Nodes move to spot automatically — the engine prefers spot capacity wherever your
            node pool allows it, with on-demand as the fallback. This button additionally nudges{' '}
            <span className="font-medium">workloads</span> toward spot on demand; drift
            self-corrects only for workloads with an explicit per-workload Spot Policy above.
          </p>
        </div>
        <button
          onClick={rebalanceNow}
          disabled={busy}
          className="shrink-0 px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium disabled:opacity-50"
        >
          {busy ? 'Rebalancing…' : 'Rebalance to Spot'}
        </button>
      </div>
      <label className="flex items-center gap-2 mt-4 text-sm text-gray-700">
        <input type="checkbox" checked={auto} onChange={(e) => toggleAuto(e.target.checked)} />
        Automatic spot rebalancing <span className="text-gray-400">(off by default)</span>
      </label>
      {msg && <p className="text-sm mt-2 text-gray-600">{msg}</p>}
    </div>
  );
}
